import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import apiService from '../../services/apiService';
import { downloadFile } from '../../utils/downloadHelpers';

/**
 * Lista de documentos de un equipo con acciones de descarga, actualización y eliminación.
 * Muestra cards con información de vencimiento, tipo de archivo y tamaño.
 * Permite exportar todos los documentos como ZIP.
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} props.equipoId - ID del equipo cuyos documentos mostrar
 * @param {Function} [props.onUploadClick] - Callback al hacer click en "Subir documento"
 * @param {Function} [props.onUpdateClick] - Callback al hacer click en "Actualizar", recibe (doc)
 * @param {Function} [props.onDeleteRequest] - Callback al solicitar eliminación, recibe (doc)
 * @param {Function} [props.onHistoryClick] - Callback al hacer click en "Historial", recibe (doc)
 * @param {Function} [props.onReportClick] - Callback al hacer click en "Reportar", recibe (doc)
 * @param {number} [props.refreshTrigger=0] - Cambiar valor para forzar recarga de documentos
 * @returns {JSX.Element}
 */
function DocumentosEquipoList({ equipoId, onUploadClick, onUpdateClick, onDeleteRequest, onHistoryClick, onReportClick, canDownload = true, refreshTrigger = 0 }) {
  const { showToast } = useToast();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);

  const fetchDocumentos = useCallback(async () => {
    if (!equipoId) return;
    setLoading(true);
    try {
      const response = await apiService.getDocumentosEquipo({ equipo_id: equipoId, limit: 1000 });
      if (response.success) {
        setDocumentos(response.data || []);
      }
    } catch (error) {
      showToast('Error al cargar documentos', 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshTrigger es intencional para forzar recarga desde el padre
  }, [equipoId, showToast, refreshTrigger]);

  useEffect(() => {
    fetchDocumentos();
  }, [fetchDocumentos]);

  // Cierra el menú al hacer click fuera del contenedor
  useEffect(() => {
    if (!openMenuId) return;
    const handleOutside = (e) => {
      if (!e.target.closest('.documento-card-menu')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleOutside);
    return () => document.removeEventListener('click', handleOutside);
  }, [openMenuId]);

  /**
   * Descarga un documento
   * @param {Object} doc - Documento a descargar
   */
  const handleDownload = async (doc) => {
    try {
      const { blob, filename } = await apiService.downloadDocumentoEquipo(doc.id);
      downloadFile(blob, filename);
    } catch (error) {
      showToast('Error al descargar documento', 'error');
    }
  };

  /**
   * Solicita eliminación de documento
   * @param {Object} doc - Documento a eliminar
   */
  const handleDelete = (doc) => {
    onDeleteRequest?.(doc);
  };

  /**
   * Exporta todos los documentos del equipo como ZIP
   */
  const handleExportAll = async () => {
    try {
      const { blob, filename } = await apiService.exportAllDocumentosEquipo(equipoId);
      downloadFile(blob, filename);
      showToast('Documentos exportados exitosamente', 'success');
    } catch (error) {
      showToast(error.message || 'Error al exportar documentos', 'error');
    }
  };

  /**
   * Alterna la visibilidad del menú de acciones secundarias de un card
   * @param {number} id - ID del documento
   * @param {React.SyntheticEvent} e - Evento de click
   */
  const toggleMenu = useCallback((id, e) => {
    e.stopPropagation();
    setOpenMenuId(prev => prev === id ? null : id);
  }, []);

  /**
   * Obtiene clase CSS según días hasta vencimiento
   * @param {string|null} fecha - Fecha de vencimiento
   * @returns {string} Clase CSS
   */
  const getVencimientoClass = (fecha) => {
    if (!fecha) return 'vencimiento-sin-vencimiento';
    const dias = Math.ceil((new Date(fecha) - new Date()) / (1000 * 60 * 60 * 24));
    if (dias < 0) return 'vencimiento-vencido';
    if (dias <= 7) return 'vencimiento-proximo';
    if (dias <= 30) return 'vencimiento-medio-plazo';
    return 'vencimiento-vigente';
  };

  /**
   * Obtiene texto e ícono según días hasta vencimiento
   * @param {string|null} fecha - Fecha de vencimiento
   * @returns {{text: string, icon: string}}
   */
  const getVencimientoInfo = (fecha) => {
    if (!fecha) return { text: 'Sin vencimiento', icon: 'fa-info-circle' };
    const dias = Math.ceil((new Date(fecha) - new Date()) / (1000 * 60 * 60 * 24));
    if (dias < 0) return { text: `Vencido hace ${Math.abs(dias)} días`, icon: 'fa-exclamation-circle' };
    if (dias === 0) return { text: 'Vence hoy', icon: 'fa-exclamation-triangle' };
    if (dias <= 7) return { text: `Vence en ${dias} días`, icon: 'fa-exclamation-triangle' };
    if (dias <= 30) return { text: `Vence en ${dias} días`, icon: 'fa-clock' };
    return { text: `Vence en ${dias} días`, icon: 'fa-check-circle' };
  };

  /**
   * Obtiene ícono FontAwesome según extensión de archivo
   * @param {string} filename - Nombre del archivo
   * @returns {string} Clase del ícono
   */
  const getFileIcon = (filename) => {
    if (!filename) return 'fa-file';
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: 'fa-file-pdf',
      doc: 'fa-file-word',
      docx: 'fa-file-word',
      xls: 'fa-file-excel',
      xlsx: 'fa-file-excel',
      jpg: 'fa-file-image',
      jpeg: 'fa-file-image',
      png: 'fa-file-image',
      gif: 'fa-file-image',
      zip: 'fa-file-archive',
      rar: 'fa-file-archive',
      txt: 'fa-file-alt',
      default: 'fa-file'
    };
    return iconMap[ext] || iconMap.default;
  };

  /**
   * Formatea bytes a formato legible (KB, MB)
   * @param {number} bytes - Tamaño en bytes
   * @returns {string} Tamaño formateado
   */
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Estado de carga
  if (loading) {
    return (
      <div className="documentos-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <span>Cargando documentos...</span>
      </div>
    );
  }

  const hasSecondaryActions = onUpdateClick || onHistoryClick || onReportClick;

  return (
    <div className="documentos-content-container">
      {/* Zona desplazable con cards o estado vacío */}
      <div className="documentos-scroll-area">
        {documentos.length === 0 ? (
          <div className="documentos-empty">
            <i className="fas fa-folder-open"></i>
            <h4>No hay documentos</h4>
            <p>Aún no se han cargado documentos para este equipo</p>
          </div>
        ) : (
          <div className="documentos-cards-grid">
            {documentos.map(doc => {
            const filename = doc.archivo_path?.split('/').pop() || 'Archivo';
            const vencClass = getVencimientoClass(doc.fecha_vencimiento);
            const vencInfo = getVencimientoInfo(doc.fecha_vencimiento);
            const fileIcon = getFileIcon(filename);

            return (
              <div
                key={doc.id}
                className="documento-card"
              >
                {/* Botón eliminar - esquina superior derecha, separado del resto de acciones */}
                {onDeleteRequest && (
                  <button
                    className="btn btn-danger documento-card-delete-btn"
                    onClick={() => handleDelete(doc)}
                    title="Eliminar documento"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}

                {/* Header: Ícono + Tipo de documento (título) + nombre de archivo (meta) */}
                <div className="documento-card-header">
                  <i className={`fas ${fileIcon} documento-file-icon`}></i>
                  <div className="documento-card-info">
                    <h5 className="documento-card-filename">
                      {doc.tipo_documento?.nombre || 'Documento'}
                    </h5>
                    <div className="documento-card-meta">
                      {doc.tamano_bytes && (
                        <>
                          <span className="documento-card-size">
                            {formatFileSize(doc.tamano_bytes)}
                          </span>
                          <span className="documento-card-separator">•</span>
                        </>
                      )}
                      <span className="documento-card-original-name" title={filename}>
                        {filename}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Badge de vencimiento - junto al header para visibilidad inmediata */}
                <div className={`documento-vencimiento-badge ${vencClass}`}>
                  <i className={`fas ${vencInfo.icon}`}></i>
                  <span>{vencInfo.text}</span>
                </div>

                {/* Botones de acción: [Descargar] + [⋮ acciones secundarias] */}
                <div className="documento-card-actions">
                  {canDownload && (
                    <button
                      className="btn btn-primary documento-btn-action-grow"
                      onClick={() => handleDownload(doc)}
                      title="Descargar documento"
                    >
                      <i className="fas fa-download"></i>
                    </button>
                  )}

                  {hasSecondaryActions && (
                    <div className="documento-card-menu">
                      <button
                        className="btn btn-secondary documento-btn-action-fixed"
                        onClick={(e) => toggleMenu(doc.id, e)}
                        title="Más acciones"
                        aria-haspopup="true"
                        aria-expanded={openMenuId === doc.id}
                      >
                        <i className="fas fa-ellipsis-v"></i>
                      </button>

                      {openMenuId === doc.id && (
                        <div className="documento-dropdown-menu">
                          {onUpdateClick && (
                            <button
                              className="documento-dropdown-item"
                              onClick={() => { onUpdateClick(doc); setOpenMenuId(null); }}
                            >
                              <i className="fas fa-sync-alt"></i>
                              Actualizar documento
                            </button>
                          )}
                          {onHistoryClick && (
                            <button
                              className="documento-dropdown-item"
                              onClick={() => { onHistoryClick(doc); setOpenMenuId(null); }}
                            >
                              <i className="fas fa-history"></i>
                              Ver historial
                            </button>
                          )}
                          {onReportClick && (
                            <button
                              className="documento-dropdown-item"
                              onClick={() => { onReportClick(doc); setOpenMenuId(null); }}
                            >
                              <i className="fas fa-flag"></i>
                              Reportar problema
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Footer sticky con botones de acción */}
      <div className="documentos-footer">
        {onUploadClick && (
          <button className="btn btn-primary" onClick={onUploadClick}>
            <i className="fas fa-upload"></i> Subir documento
          </button>
        )}
        <button className="btn btn-secondary" onClick={handleExportAll}>
          <i className="fas fa-download"></i> Exportar todo
        </button>
      </div>
    </div>
  );
}

export default DocumentosEquipoList;
